import {
    addDays,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInYears,
    format,
    isSameDay,
    isSameYear,
    subDays,
} from "date-fns";

export function formatDefaultDate(date: Date, now: Date = new Date()) {
    if (isSameYear(date, now)) {
        return format(date, "d MMM");
    }
    return format(date, "d MMM yyyy");
}

export function formatDefaultTime(date: Date, now: Date = new Date()) {
    return format(date, "HH:mm");
}

export function formatDefaultDateTime(date: Date, now: Date = new Date()) {
    return `${formatDefaultDate(date, now)}, ${formatDefaultTime(date, now)}`;
}

export function singularPlural(singular: string, plural: string, count: number) {
    return Math.abs(count) === 1 ? singular : plural;
}

export function formatDateComparison(date: Date, now: Date = new Date()) {
    if (isSameDay(date, now)) {
        return "today";
    }
    const tomorrow = addDays(now, 1);
    if (isSameDay(date, tomorrow)) {
        return "tomorrow";
    }
    const yesterday = subDays(now, 1);
    if (isSameDay(date, yesterday)) {
        return "yesterday";
    }

    const isInFuture = date > now;
    if (isInFuture) {
        const differenceDays = differenceInDays(date, now);
        if (differenceDays <= 30) {
            return `in ${differenceDays} ${singularPlural(
                "day",
                "days",
                differenceDays,
            )}`;
        }
        const differenceMonths = differenceInMonths(date, now);
        if (differenceMonths < 12) {
            return `in ${differenceMonths} ${singularPlural(
                "month",
                "months",
                differenceMonths,
            )}`;
        }
        const differentYears = differenceInYears(date, now);
        return `in ${differentYears} ${singularPlural(
            "year",
            "years",
            differentYears,
        )}`;
    }

    const differenceDays = differenceInDays(now, date);
    if (differenceDays <= 30) {
        return `${differenceDays} ${singularPlural("day", "days", differenceDays)} ago`;
    }
    const differenceMonths = differenceInMonths(now, date);
    if (differenceMonths < 12) {
        return `${differenceMonths} ${singularPlural(
            "month",
            "months",
            differenceMonths,
        )} ago`;
    }
    const differentYears = differenceInYears(now, date);
    return `${differentYears} ${singularPlural("year", "years", differentYears)} ago`;
}

export function formatTimeComparison(date: Date, now: Date = new Date()) {
    const diffMinutes = differenceInMinutes(now, date);
    const isNow = diffMinutes >= -1 && diffMinutes <= 1;
    if (isNow) {
        return "now";
    }

    const isInFuture = date > now;
    if (isInFuture) {
        const minutes = differenceInMinutes(date, now);
        if (minutes < 60) {
            return `in ${minutes} min`;
        }
        const hours = Math.abs(differenceInHours(date, now));
        if (hours < 24) {
            return `in ${hours} hr`;
        }
        const days = Math.abs(differenceInDays(date, now));
        if (days <= 30) {
            return `in ${days} ${singularPlural("day", "days", days)}`;
        }

        return formatDateComparison(date, now);
    }

    const minutes = differenceInMinutes(now, date);
    if (minutes < 60) {
        return `${minutes} min ago`;
    }
    const hours = Math.abs(differenceInHours(now, date));
    if (hours < 24) {
        return `${hours} hr ago`;
    }
    const days = Math.abs(differenceInDays(now, date));
    if (days <= 30) {
        return `${days} ${singularPlural("day", "days", days)} ago`;
    }

    return formatDateComparison(date, now);
}

export function formatToRelativeAndAbsoluteLabel(
    date: Date,
    now: Date = new Date(),
): [string, string] {
    const diffMinutes = differenceInMinutes(date, now);
    if (diffMinutes <= 1) {
        return ["Now", formatDefaultTime(date, now)];
    }
    if (diffMinutes <= 55) {
        return [`In ${diffMinutes} min`, formatDefaultTime(date, now)];
    }
    if (diffMinutes > 55 && diffMinutes < 90) {
        return ["In 1 hour", formatDefaultTime(date, now)];
    }
    if (diffMinutes >= 90 && isSameDay(date, now)) {
        const diffHours = Math.round(diffMinutes / 60);
        return [`In ${diffHours} hours`, formatDefaultTime(date, now)];
    }
    if (isSameDay(date, addDays(now, 1))) {
        return ["Tomorrow", formatDefaultTime(date, now)];
    }
    if (differenceInHours(date, now) >= 6 * 24) {
        return ["Next week", formatDefaultDate(date, now)];
    }
    return [
        `In ${Math.round(differenceInHours(date, now) / 24)} days`,
        formatDefaultDate(date, now),
    ];
}
